<app-sidebar>
  <div class="my-account-container">
    <h1>My Account</h1>
    <div class="cards-stack">
      <p *ngIf="awaitingPaymentConfirmation" class="pending-payment-text">
        {{ awaitingPaymentMessage || 'Waiting for payment confirmation...' }}
      </p>

      <mat-card class="my-account-card">
      <mat-card-content>
        <div *ngIf="loading" class="loading-wrap">
          <mat-spinner diameter="30"></mat-spinner>
          <span>Loading profile...</span>
        </div>

        <form *ngIf="!loading" [formGroup]="profileForm" (ngSubmit)="onUpdate()" class="profile-form">
          <div class="form-grid">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Name</mat-label>
              <input matInput formControlName="name" autocomplete="given-name">
              <mat-error *ngIf="profileForm.get('name')?.hasError('required')">Name is required</mat-error>
              <mat-error *ngIf="profileForm.get('name')?.hasError('maxlength')">
                Name must be at most 100 characters
              </mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Surname</mat-label>
              <input matInput formControlName="surname" autocomplete="family-name">
              <mat-error *ngIf="profileForm.get('surname')?.hasError('required')">Surname is required</mat-error>
              <mat-error *ngIf="profileForm.get('surname')?.hasError('maxlength')">
                Surname must be at most 100 characters
              </mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Email</mat-label>
              <input matInput formControlName="email" name="username" autocomplete="username" readonly>
              <mat-hint>Email cannot be changed</mat-hint>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>ID Number</mat-label>
              <input matInput formControlName="idNumber" maxlength="13" autocomplete="off">
              <mat-hint>South African ID number (13 digits)</mat-hint>
              <mat-error *ngIf="profileForm.get('idNumber')?.hasError('pattern')">
                ID Number must be exactly 13 digits
              </mat-error>
            </mat-form-field>
          </div>

          <div class="action-row">
            <button mat-raised-button color="primary" type="submit" [disabled]="!canUpdate">
              {{ saving ? 'Updating...' : 'Update' }}
            </button>
          </div>
        </form>
      </mat-card-content>
      </mat-card>

      <mat-card class="my-account-card" *ngIf="billingProfile as bp">
        <mat-card-header>
          <mat-card-title class="card-heading-large">Billing Summary</mat-card-title>
        </mat-card-header>
        <mat-card-content class="billing-grid">
        <div class="billing-pending-banner" *ngIf="isBillingPendingCancellation" role="status">
          Cancellation is scheduled. You still have access until the end of this billing cycle. Use
          <strong>Re-enable</strong> in Manage Subscription to undo before then.
        </div>
        <div class="billing-pending-banner" *ngIf="hasRecurringPaymentIssue" role="alert">
          {{ recurringPaymentIssueMessage }}
        </div>
        <div class="plan-row">
          <strong>Current plan:</strong>
          <span>{{ displayCurrentPlan }}</span>
        </div>
        <div class="status-row">
          <div>
            <strong>Status:</strong> {{ displayBillingStatus }}
          </div>
        </div>
        <div><strong>Next payment:</strong> {{ displayNextPaymentDate }}</div>
        <div><strong>Payment method:</strong> {{ displayPaymentMethod }}</div>
        <div class="team-seats-section" *ngIf="showTeamSeatBreakdown">
          <strong class="team-seats-heading">Team seats ({{ teamSeatBreakdown.length }})</strong>
          <div class="team-seats-table-wrap">
            <table class="team-seats-table">
              <thead>
                <tr>
                  <th scope="col">Member</th>
                  <th scope="col">Role</th>
                  <th scope="col">Account type</th>
                  <th scope="col">Monthly cost</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let seat of teamSeatBreakdown">
                  <td>
                    <span class="seat-name">{{ seat.displayName }}</span>
                    <span class="seat-email" *ngIf="seat.email">{{ seat.email }}</span>
                  </td>
                  <td>
                    {{ seat.role }}
                    <span class="seat-billing-tag" *ngIf="seat.isBillingOwner"> · billing</span>
                  </td>
                  <td>{{ seat.isBillingOwner ? 'Billing owner' : 'Team member' }}</td>
                  <td>{{ formatSeatMonthlyCost(seat.monthlyPriceCents) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="3"><strong>Total per month</strong></td>
                  <td><strong>{{ displayTeamMonthlyTotal }}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
        </mat-card-content>
      </mat-card>

      <mat-card class="my-account-card" *ngIf="showManageSubscriptionCard">
        <mat-card-header>
          <mat-card-title class="card-heading-large">Manage Subscription</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <p *ngIf="isTeamMemberManagedByAdmin">Managed by Admin</p>
          <p *ngIf="!isTeamMemberManagedByAdmin && isBillingCancelled">
            Your subscription is cancelled. Resubscribe to restore billing and monthly credits.
          </p>
          <p *ngIf="isTeamBillingOwner && !isBillingCancelled">
            <ng-container *ngIf="isInvoiceBillingType; else teamBillingExemptOrAutopayCopy">
              Team billing is set to invoice mode. Charges accrue monthly and are settled via invoice workflow.
            </ng-container>
            <ng-template #teamBillingExemptOrAutopayCopy>
              <ng-container *ngIf="isBillingPaymentExempt; else teamAutopayCopy">
              Team billing is marked as payment-exempt. Your billing cycle and credit refresh still run monthly.
              </ng-container>
            </ng-template>
            <ng-template #teamAutopayCopy>
              Team billing is charged automatically on your monthly anniversary using your saved card.
            </ng-template>
          </p>
          <p *ngIf="!isTeamMemberManagedByAdmin && isBillingPendingCancellation">
            Your subscription will not renew next month. Re-enable to continue automatic billing, or manage your card in Paystack.
          </p>
          <p *ngIf="!isTeamMemberManagedByAdmin && !isTeamBillingOwner && !isBillingCancelled && !isBillingPendingCancellation">
            Manage your payment method in Paystack, or cancel your subscription below.
          </p>
          <div class="subscription-actions" *ngIf="!isTeamMemberManagedByAdmin">
            <button
              *ngIf="isBillingCancelled"
              mat-raised-button
              color="primary"
              type="button"
              (click)="onResubscribe()">
              Resubscribe
            </button>
            <button
              *ngIf="isBillingPendingCancellation"
              mat-raised-button
              color="primary"
              type="button"
              [disabled]="!canReenableSubscription || reenablingSubscription"
              (click)="onReenableSubscription()">
              {{ reenablingSubscription ? 'Re-enabling...' : 'Re-enable' }}
            </button>
            <button
              *ngIf="(isTeamBillingOwner || shouldShowIndividualRecurringRecovery) && !isBillingCancelled && !isBillingPaymentExempt && !isInvoiceBillingType"
              mat-stroked-button
              color="primary"
              type="button"
              [disabled]="updatingCard"
              (click)="onUpdateCard()">
              {{ updatingCard ? 'Opening...' : (isTeamBillingOwner ? 'Update Card' : 'Retry Card Setup') }}
            </button>
            <a
              *ngIf="!isBillingCancelled && !isTeamBillingOwner"
              mat-stroked-button
              color="primary"
              [href]="manageSubscriptionUrl"
              [attr.aria-disabled]="!manageSubscriptionUrl || loadingManageSubscriptionUrl ? true : null"
              [style.pointer-events]="!manageSubscriptionUrl || loadingManageSubscriptionUrl ? 'none' : null"
              [style.opacity]="!manageSubscriptionUrl || loadingManageSubscriptionUrl ? 0.6 : null"
              target="_blank"
              rel="noopener noreferrer">
              {{ loadingManageSubscriptionUrl ? 'Loading...' : 'Manage Subscription' }}
            </a>
            <button
              mat-stroked-button
              color="warn"
              type="button"
              *ngIf="canCancelSubscription && !isTeamBillingOwner"
              [disabled]="cancellingSubscription"
              (click)="onCancelSubscription()">
              {{ cancellingSubscription ? 'Cancelling...' : 'Cancel Subscription' }}
            </button>
          </div>
        </mat-card-content>
      </mat-card>

      <mat-card class="my-account-card" *ngIf="billingProfile as bp">
        <mat-card-header>
          <mat-card-title class="card-heading-large">Credits and Topup</mat-card-title>
        </mat-card-header>
        <mat-card-content class="billing-grid">
        <div
          class="zero-credits-banner"
          *ngIf="hasZeroCredits"
          role="status">
          No credits yet — top up below to get started.
        </div>
        <div><strong>Included remaining:</strong> {{ bp.credits.includedRemaining }}</div>
        <div><strong>Prepaid remaining:</strong> {{ bp.credits.prepaidRemaining }}</div>
        <div><strong>Total available:</strong> {{ bp.credits.totalRemaining }}</div>
        <div><strong>Expiry Date:</strong> {{ displayExpiryDate }}</div>
        <div class="topup-section">
          <p *ngIf="!isTeamUser">
            Buy prepaid credits. These are consumed before monthly included credits and are valid for 90 days after purchase.
          </p>
          <p *ngIf="isTeamUser">
            Buy your own prepaid credits.<br>
            These are consumed before monthly team credits.
          </p>
          <app-topup-pack-cards
            [packs]="topupPackOptions"
            [selectedPackSize]="selectedTopupPack"
            [disabled]="purchasingTopup"
            [showSkip]="false"
            [buyLabel]="purchasingTopup ? 'Opening...' : 'Top Up Account'"
            (selectedPackSizeChange)="onSelectedPackChange($event)"
            (buy)="onBuyPackCredits($event)">
          </app-topup-pack-cards>
        </div>
        </mat-card-content>
      </mat-card>

      <mat-card class="my-account-card" *ngIf="billingLedger?.entries?.length">
        <mat-card-header>
          <mat-card-title>Recent Activity</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="ledger-table-wrap">
            <table class="ledger-table">
              <thead>
                <tr>
                  <th scope="col">Type</th>
                  <th scope="col">Credits</th>
                  <th scope="col">Date</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let entry of billingLedger?.entries">
                  <td>{{ entry.entryType }}</td>
                  <td>{{ entry.direction === 'DEBIT' ? '-' : '+' }}{{ entry.amountCredits }}</td>
                  <td>{{ entry.createdAt | date:'medium' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </mat-card-content>
      </mat-card>

    </div>
  </div>
</app-sidebar>
